<script setup lang="ts">
  /**
   * The entire "Default Invoice Currency" setting, end-to-end:
   * card row + edit modal + form + submit. One file = one setting.
   *
   * Wires into the shared agency-settings data layer via
   * `useAgencySettings()` — that composable owns the scoped-settings
   * map, upsert, remove. This SFC owns everything else about this
   * specific setting.
   */

  import { computed, ref, watch } from 'vue'
  import { IconEdit } from '@gat-ship/icons'
  import {
    Button,
    FormField,
    FormSubmit,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { CurrencyPicker } from '@gat-ship/ref-data'
  import type { ScopedSetting, TypedSettingValues } from '@gat-ship/types'
  import SettingCard from './SettingCard.vue'
  import SettingOverrideCard from './SettingOverrideCard.vue'
  import { useAgencySettings } from '../composables/useAgencySettings'
  import { useSettingDefinitions } from '../composables/useSettingDefinitions'
  import { useSettingsHelpers } from '../composables/useSettingsHelpers'
  import { useAgencyStructureContext } from '../composables/useAgencyStructureContext'

  const SETTING_NAME = 'client.billing.invoicing.defaultCurrency'
  type Value = TypedSettingValues[typeof SETTING_NAME]
  const TITLE = 'Default Invoice Currency'
  const DESCRIPTION =
    'Sets the default currency for invoices created by this agency.'

  const agencySettings = useAgencySettings()
  const settingDefinitions = useSettingDefinitions()
  const agencyStructure = useAgencyStructureContext()
  const {
    getScopedSettingValue,
    getInheritLabel,
    resolveInheritedFrom,
    canRevertToInherit
  } = useSettingsHelpers()

  // Fetch on mount + on agency change. The composable dedups identical
  // in-flight requests so a sibling setting SFC doing the same thing
  // doesn't cause a duplicate HTTP call.
  watch(
    agencySettings.agencyId,
    (id) => {
      if (id) void agencySettings.refetch([SETTING_NAME])
    },
    { immediate: true }
  )

  const scopedSetting = computed(
    () =>
      agencySettings.scopedSettings[SETTING_NAME] as
        | ScopedSetting<Value>
        | undefined
  )
  const definition = computed(() => settingDefinitions.get(SETTING_NAME))

  // --- Card display state ---
  const isLoading = computed(
    () => agencySettings.isLoading.value && !scopedSetting.value
  )

  const valueText = computed(() => {
    if (isLoading.value) return 'Pending...'
    const value = getScopedSettingValue(scopedSetting.value)
    if (!value) return 'Not set'
    return value
  })

  const statusText = computed(() => {
    if (isLoading.value) return 'Loading...'
    const s = scopedSetting.value
    if (!s) return 'Not found'
    if (s.layerStatus === 'forbidden') return 'Forbidden'
    if (s.layerStatus === 'assigned') return 'Assigned'
    const firstParent = s.parentChain[0]
    if (!firstParent) {
      return definition.value?.hasDefaultValue ? 'Gatship Default' : 'Not set'
    }
    return firstParent.layerScope === 'default'
      ? 'Gatship Default'
      : 'Inherited'
  })

  // --- Modal ---
  const isOpen = ref(false)

  const schema = z
    .object({
      mode: z.enum(['inherit', 'custom']),
      currencyCode: z.string().optional()
    })
    .superRefine((data, ctx) => {
      if (
        data.mode === 'custom' &&
        (!data.currencyCode || data.currencyCode.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Currency is required',
          path: ['currencyCode']
        })
      }
    })
  type FormValues = z.infer<typeof schema>

  const form = useForm({
    defaultValues: {
      mode: 'inherit',
      currencyCode: ''
    } as FormValues,
    validators: { onChange: schema },
    onSubmit: async ({ value }) => {
      if (value.mode === 'inherit') {
        await agencySettings.remove(SETTING_NAME)
      } else if (value.mode === 'custom' && value.currencyCode) {
        await agencySettings.upsert(SETTING_NAME, value.currencyCode as Value)
      }
      isOpen.value = false
    }
  })

  // --- Inherit card details ---
  const inherited = computed(() =>
    resolveInheritedFrom(
      scopedSetting.value,
      agencyStructure.agencyStructure.value,
      agencySettings.agencyId.value
    )
  )
  const inheritLabel = computed(() => getInheritLabel(scopedSetting.value))
  const canInherit = computed(() =>
    canRevertToInherit(
      definition.value,
      agencyStructure.agencyStructure.value,
      agencySettings.agencyId.value
    )
  )
  const inheritedValueText = computed(() => {
    const v = inherited.value?.value
    return typeof v === 'string' ? v : undefined
  })

  function openModal() {
    const current = scopedSetting.value
    if (current?.layerStatus === 'assigned') {
      form.reset({ mode: 'custom', currencyCode: current.value })
    } else {
      form.reset({
        mode: canInherit.value ? 'inherit' : 'custom',
        currencyCode: ''
      })
    }
    isOpen.value = true
  }
</script>

<template>
  <SettingCard
    :title="TITLE"
    :description="DESCRIPTION"
    :value-text="valueText"
    :status-text="statusText"
    :is-loading="isLoading"
    @click="openModal"
  />
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconEdit class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        {{ TITLE }}
      </ModalHeaderTitle>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <SettingOverrideCard
          :form="form"
          name="mode"
          :inherit-label="inheritLabel"
          :can-revert-to-inherit="canInherit"
          :inherited-from-text="inherited?.inheritedFrom"
          :inherited-value-text="inheritedValueText"
        >
          <template #custom>
            <FieldGroup>
              <FormField
                :form="form"
                name="currencyCode"
                label="Currency"
                required
              >
                <template #default="{ field, invalid }">
                  <CurrencyPicker
                    :id="field.name"
                    :model-value="field.state.value"
                    :aria-invalid="invalid"
                    @update:model-value="field.handleChange"
                    @blur="field.handleBlur"
                  />
                </template>
              </FormField>
            </FieldGroup>
          </template>
        </SettingOverrideCard>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save" :require-dirty="false" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
